import React from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import { Wrench } from "lucide-react";
import logo from "../../assets/images/logo.webp";

const UnderMaintenance = ({
  logoSrc = logo,
  logoAlt = "Company Logo",
  mainHeading = "Under Maintenance",
  subHeading = "We're working hard to improve our system. Please check back soon!",
  companyName = "iDiag Pvt Ltd",
}) => {
  const currentYear = new Date().getFullYear();

  const styles = {
    container: {
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      minHeight: "100vh",
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      position: "relative",
      overflow: "hidden",
    },
    backgroundPattern: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 1px, transparent 1px),
                        radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 1px, transparent 1px),
                        radial-gradient(circle at 40% 80%, rgba(255,255,255,0.1) 1px, transparent 1px)`,
      backgroundSize: "100px 100px, 150px 150px, 120px 120px",
      animation: "float 20s ease-in-out infinite",
    },
    logoContainer: {
      animation: "fadeInDown 1s ease-out",
    },
    logo: {
      maxHeight: "100px",
      width: "auto",
      filter: "drop-shadow(0 10px 20px rgba(0,0,0,0.1))",
      transition: "transform 0.3s ease",
      borderRadius: "50%",
    },
    card: {
      border: "none",
      borderRadius: "20px",
      background: "rgba(255, 255, 255, 0.95)",
      backdropFilter: "blur(10px)",
      boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
      animation: "fadeInUp 1s ease-out 0.3s both",
    },
    iconContainer: {
      width: "100px",
      height: "100px",
      background: "linear-gradient(135deg, #007bff, #0056b3)",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "0 auto 1.5rem",
      boxShadow: "0 10px 30px rgba(0,123,255,0.3)",
      animation:
        "pulse 2s ease-in-out infinite, rotateWrench 3s ease-in-out infinite",
      position: "relative",
    },
    iconContainerBefore: {
      content: '""',
      position: "absolute",
      top: "-2px",
      left: "-2px",
      right: "-2px",
      bottom: "-2px",
      background: "linear-gradient(45deg, #007bff, #28a745, #ffc107, #dc3545)",
      borderRadius: "50%",
      zIndex: -1,
      animation: "rotate 4s linear infinite",
    },
    heading: {
      fontSize: "2.5rem",
      fontWeight: "700",
      background: "linear-gradient(135deg, #333, #666)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
      marginBottom: "1rem",
      animation: "fadeIn 1s ease-out 0.6s both",
    },
    subheading: {
      fontSize: "1.1rem",
      color: "#6c757d",
      lineHeight: "1.6",
      animation: "fadeIn 1s ease-out 0.9s both",
    },
    footer: {
      animation: "fadeIn 1s ease-out 1.2s both",
    },
    badge: {
      fontSize: "0.85rem",
      padding: "0.7rem 1.5rem",
      borderRadius: "25px",
      background: "linear-gradient(135deg, #ffc107, #ff8c00)",
      border: "none",
      color: "#333",
      fontWeight: "600",
      boxShadow: "0 5px 15px rgba(255,193,7,0.3)",
      animation: "bounce 2s ease-in-out infinite",
    },
    progressBar: {
      width: "200px",
      height: "4px",
      background: "rgba(255,255,255,0.3)",
      borderRadius: "2px",
      margin: "1.5rem auto",
      overflow: "hidden",
      position: "relative",
    },
    progressBarFill: {
      height: "100%",
      background: "linear-gradient(90deg, #007bff, #28a745)",
      borderRadius: "2px",
      animation: "loading 2s ease-in-out infinite",
    },
  };

  const keyframes = `
    @keyframes fadeInDown {
      from { opacity: 0; transform: translateY(-30px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(30px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    
    @keyframes pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.05); }
    }
    
    @keyframes rotateWrench {
      0%, 100% { transform: rotate(0deg); }
      25% { transform: rotate(-10deg); }
      75% { transform: rotate(10deg); }
    }
    
    @keyframes rotate {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    
    @keyframes bounce {
      0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
      40% { transform: translateY(-5px); }
      60% { transform: translateY(-3px); }
    }
    
    @keyframes loading {
      0% { transform: translateX(-100%); }
      50% { transform: translateX(0%); }
      100% { transform: translateX(100%); }
    }
    
    @keyframes float {
      0%, 100% { transform: translateY(0px) rotate(0deg); }
      33% { transform: translateY(-10px) rotate(1deg); }
      66% { transform: translateY(5px) rotate(-1deg); }
    }
  `;

  return (
    <>
      <style>{keyframes}</style>
      <div
        className="min-vh-100 d-flex align-items-center"
        style={styles.container}
      >
        <div style={styles.backgroundPattern}></div>

        <Container style={{ position: "relative", zIndex: 1 }}>
          <Row className="justify-content-center">
            <Col lg={6} md={8}>
              <div className="text-center mb-4" style={styles.logoContainer}>
                <img
                  src={logoSrc}
                  alt={logoAlt}
                  className="img-fluid mb-3"
                  style={styles.logo}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = "scale(1.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = "scale(1)";
                  }}
                />
              </div>

              <Card style={styles.card}>
                <Card.Body className="p-5 text-center">
                  <div className="mb-4">
                    <div style={styles.iconContainer}>
                      <div style={styles.iconContainerBefore}></div>
                      <Wrench size={40} color="white" />
                    </div>

                    <h1 style={styles.heading}>{mainHeading}</h1>

                    <p style={styles.subheading}>{subHeading}</p>
                    <div style={styles.progressBar}>
                      <div style={styles.progressBarFill}></div>
                    </div>

                    <div className="mt-4">
                      <small className="text-muted">
                        System updates in progress...
                      </small>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* Footer */}
              <div className="text-center mt-4" style={styles.footer}>
                <p
                  className="text-light small mb-3"
                  style={{ textShadow: "0 1px 3px rgba(0,0,0,0.3)" }}
                >
                  © {currentYear} {companyName}. All rights reserved.
                </p>
                <div className="d-flex justify-content-center align-items-center">
                  <span style={styles.badge}>Maintenance Mode</span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default UnderMaintenance;
